import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { forwardRef, useEffect, useRef, useState } from "react";
import { getTextSizeClasses } from "./utils";
export const sharedTextStyles = {
    fontFamily: 'inherit',
    letterSpacing: 'normal',
    wordSpacing: 'normal',
    textRendering: 'geometricPrecision',
    WebkitFontSmoothing: 'antialiased',
    MozOsxFontSmoothing: 'grayscale',
    boxSizing: 'border-box',
    whiteSpace: 'pre-wrap',
    overflowWrap: 'break-word',
    wordBreak: 'normal',
    padding: 0,
    margin: 0,
    textTransform: 'none',
    textIndent: 0,
    textShadow: 'none',
    textAlign: 'left',
    display: 'block',
    appearance: 'none',
    WebkitAppearance: 'none',
    MozAppearance: 'none',
};
export const AutosizedTextarea = forwardRef(({ label = '', value = '', placeholder = '', onChange, onBlur, onKeyDown, autoFocus = true, minWidthSource = 'both', textElement = 'span', preventNewlines = true, className = '', style, ...textareaProps }, ref) => {
    const [content, setContent] = useState(value);
    const widthHolderRef = useRef(null);
    const internalRef = useRef(null);
    const textareaRef = ref || internalRef;
    useEffect(() => {
        if (autoFocus && textareaRef.current) {
            setTimeout(() => {
                if (textareaRef.current) {
                    textareaRef.current.focus();
                    textareaRef.current.select();
                }
            }, 0);
        }
    }, [autoFocus, textareaRef]);
    useEffect(() => {
        setContent(value || '');
    }, [value]);
    const textSizeClasses = getTextSizeClasses(textElement);
    const getMinWidthContent = () => {
        switch (minWidthSource) {
            case 'placeholder':
                return placeholder;
            case 'value':
                return content.length === 0 && placeholder ? placeholder : content;
            case 'both':
            default:
                return placeholder.length > content.length ? placeholder : content;
        }
    };
    const minWidthContent = getMinWidthContent();
    const widthCalculationContent = content.length > minWidthContent.length ? content : minWidthContent;
    const handleTextareaChange = (e) => {
        const newValue = e.target.value;
        setContent(newValue);
        if (onChange) {
            onChange(e);
        }
    };
    const handleTextareaBlur = (e) => {
        if (onBlur) {
            onBlur(e);
        }
    };
    const handleKeyDown = (e) => {
        if (preventNewlines && e.key === 'Enter') {
            e.preventDefault();
        }
        if (onKeyDown) {
            onKeyDown(e);
        }
    };
    const contentLines = (() => {
        if (!widthCalculationContent || widthCalculationContent.trim() === '') {
            return [_jsx("div", { children: '\u00A0' }, "empty-line")];
        }
        return widthCalculationContent.split('\n').map((item, index) => {
            if (item.length === 0) {
                return (_jsxs(React.Fragment, { children: [_jsx("div", { children: '\u00A0' }), " "] }, `${index}-${item}`));
            }
            return _jsx("div", { children: item }, `${index}-${item}`);
        });
    })();
    return (_jsxs("div", { className: `relative inline-block w-full ${textSizeClasses} ${className}`, children: [_jsx("div", { ref: widthHolderRef, className: "invisible whitespace-pre-wrap p-0 m-0 text-inherit font-inherit leading-inherit break-words", style: sharedTextStyles, "aria-hidden": "true", children: contentLines }), _jsx("textarea", { ref: textareaRef, value: content, placeholder: placeholder, onChange: handleTextareaChange, onBlur: handleTextareaBlur, onKeyDown: handleKeyDown, className: "absolute top-0 left-0 w-full h-full p-0 border-none resize-none overflow-hidden bg-transparent focus:outline-none text-inherit font-inherit leading-inherit placeholder:text-gray-500 placeholder:opacity-80", style: sharedTextStyles, "aria-label": label, ...textareaProps })] }));
});
